// src/components/shared/AboutPage.tsx
// דף אודות CARTAX - מידע על החברה והשירותים

import React from "react";
import { useNavigate } from "react-router-dom";
import { useSeo } from "../../hooks/useSeo";

const AboutPage: React.FC = () => {
  const navigate = useNavigate();
  useSeo({
    title: "אודות CarTax — מחשבון מיסי יבוא רכב לישראל",
    description:
      "הכירו את CarTax: כלי מקצועי לחישוב מדויק של מיסי יבוא רכב — מכס, מס קנייה ומע\"מ, לייבוא אישי ומסחרי.",
  });

  return (
    <div
      className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8"
      dir="rtl"
    >
      <div className="max-w-4xl mx-auto">
        {/* כפתור חזור */}
        <button
          onClick={() => navigate(-1)}
          className="mb-6 inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
        >
          <svg
            className="w-5 h-5 ml-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          <span className="font-medium">חזור</span>
        </button>

        {/* כותרת ראשית */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <h1 className="text-4xl font-bold text-blue-900 mb-8 text-center">
            אודות CARTAX
          </h1>
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 text-center">
            המומחים למיסוי רכב ויבוא כלי רכב לישראל
          </h2>
        </div>

        {/* תוכן */}
        <div className="bg-white rounded-lg shadow-lg p-8 space-y-8">
          {/* היסטוריה */}
          <section>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              <strong>CARTAX</strong> הוקמה על־ידי <strong>בן ברוק</strong>,
              סוכן מכס ויזם בתחום יבוא הרכב, במטרה להנגיש לציבור את כל הידע
              המקצועי הקשור למיסוי רכב, מכס, ויבוא אישי ומסחרי של כלי רכב.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              בתור מי שמכיר מקרוב את המערכת, את התקנות ואת דרך הפעולה של רשות
              המיסים, בן ברוק שם לו למטרה להפוך תחום מורכב ומבלבל – לתחום{" "}
              <strong>ברור, מדויק ונגיש</strong>.
            </p>
          </section>

          <hr className="border-gray-300" />

          {/* פלטפורמת המיסוי */}
          <section>
            <h3 className="text-2xl font-bold text-blue-900 mb-6">
              פלטפורמת המיסוי המתקדמת בישראל
            </h3>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              המערכת שלנו מחשבת בדיוק את כל מרכיבי העלות של יבוא רכב:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start">
                <svg
                  className="w-6 h-6 text-blue-600 ml-3 mt-1 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-lg text-gray-700">מכס</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-6 h-6 text-blue-600 ml-3 mt-1 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-lg text-gray-700">מס קנייה</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-6 h-6 text-blue-600 ml-3 mt-1 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-lg text-gray-700">מס יוקרה</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-6 h-6 text-blue-600 ml-3 mt-1 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-lg text-gray-700">מע״מ</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-6 h-6 text-blue-600 ml-3 mt-1 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-lg text-gray-700">
                  היטלים ורכיבי מכס נוספים
                </span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-6 h-6 text-blue-600 ml-3 mt-1 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-lg text-gray-700">
                  ועוד מרכיבים המשפיעים על העלות הסופית של יבוא כלי רכב לישראל
                </span>
              </li>
            </ul>
            <p className="text-lg text-gray-700 leading-relaxed mt-6">
              המערכת נבנתה בהתבסס על ניסיון ממשי בסיווג מכס, טיפול בשחרור כלי
              רכב מהמכס, ניתוח שיטות חישוב המס, ויישום בפועל של כללי המכס ומיסוי
              הרכב.
            </p>
          </section>

          <hr className="border-gray-300" />

          {/* למה CARTAX */}
          <section>
            <h3 className="text-2xl font-bold text-blue-900 mb-4">
              למה CARTAX?
            </h3>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              כיום יותר ויותר ישראלים בוחרים לייבא רכב מחו״ל, ולכן חשוב להבין
              בצורה מדויקת את שיטת המיסוי, את חישובי המכס ואת כל מרכיבי העלות
              המשפיעים על מחיר הרכב המיובא.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              <strong>CARTAX</strong> מספקת לציבור כלי{" "}
              <strong>מקצועי, אמין ושקוף</strong> שמאפשר לדעת בדיוק כמה מס
              תשלמו, איפה אפשר לחסוך, ומהם כל השלבים בתהליך יבוא רכב לישראל.
            </p>
          </section>

          <hr className="border-gray-300" />

          {/* החזון */}
          <section>
            <h3 className="text-2xl font-bold text-blue-900 mb-4">
              החזון שלנו
            </h3>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              להוביל שינוי אמיתי בשוק מיסוי הרכב בישראל, ליצור{" "}
              <strong>שקיפות מלאה</strong> מול הציבור, ולהעניק לכל אדם – מהיבואן
              הפרטי ועד היבואן המסחרי – אפשרות לקבל החלטות מבוססות ידע.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              <strong>CARTAX</strong> אינה רק מחשבון; היא{" "}
              <strong>מרכז ידע מקצועי</strong> בתחום מכס ורכב, המעניק פתרונות
              ומידע מדויק לכל מי שמעוניין לייבא רכב בצורה חכמה, כלכלית ובטוחה.
            </p>
          </section>
        </div>

        {/* כפתור חזור תחתון */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors shadow-md font-medium"
          >
            חזור לדף הקודם
          </button>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
